import React, { FormEvent, useEffect, useState, useRef, ChangeEvent, RefObject } from 'react';

import clsx from 'clsx';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useLocale, useTranslations } from 'next-intl';

import FooterText from 'components/payment/components/FooterText';
import OptionalProduct from 'components/payment/components/OptionalProduct';
import PayingMemberInfo from 'components/payment/components/PayingMemberInfo';
import getPackagePurchasingBlockAndWarningMessage from 'components/payment/components/PaymentForm/getPackagePurchasingBlockAndWarningMessage';
import getTotalIntroductionPrice from 'components/payment/components/PaymentForm/getTotalIntroductionPrice';
import PaymentMethods from 'components/payment/components/PaymentMethods';
import { StripePaymentRef } from 'components/payment/components/StripePaymentContainer/StripePayment';
import TermsConditions from 'components/payment/components/TermsConditions';
import TotalPriceInformation from 'components/payment/components/TotalPriceInformation';
import { PaymentFormProvider } from 'contexts/PaymentFormContext';
import useTenant from 'hooks/useTenant';
import {
  BillingAddressErrorType,
  CheckoutStateOptionalProductType,
  CheckoutStateType,
  CheckoutStepInputFieldConfig,
  PhoneNumberDataErrorType,
  VippsPaymentDataErrorType,
  VippsPaymentDataType,
} from 'types/checkout';
import { MemberAsCompanyType, MemberCompanyInfoErrorType } from 'types/member';
import {
  CreateFakturaPaymentPayload,
  OptionalProductAdjustQuantityType,
  ProductPackageDetailsDataType,
} from 'types/productPackage';
import { PlanProductType } from 'types/subscriptionPlans';
import ConfirmationDialog from 'ui/ConfirmationDialog';
import Secure from 'ui/Icon/icons/Secure';
import Loader from 'ui/Loader';
import { api } from 'utils/api';
import {
  AccountCheckDto,
  AccountCheckStatus,
  CHECKOUT_STATE_STATUS,
  CheckoutUser,
  ErrorResponse,
  FakturaPaymentResponseDto,
  FieldError,
  MemberPersonalInfo,
  NetsCreateIntentResponse,
  PAYMENT_PROVIDER,
  PermissionLevelForBuyingMultipleSubscriptions,
  PermissionLevelForBuyingSamePackage,
  ProfileAddress,
  VippsCreateIntentResponse,
} from 'utils/GeneratedApi';
import { getFilteredOptionalProducts } from 'utils/getFilteredOptionalProducts';
import { normalizeProductQuantities } from 'utils/normalizeProductQuantities';
import validateCity from 'utils/validateCity';
import validateEmail from 'utils/validateEmail';
import validateMobilePhoneNr from 'utils/validateMobilePhoneNr';
import validateName from 'utils/validateName';
import validateOrganisationNo from 'utils/validateOrganisationNo';
import validatePostalCode from 'utils/validatePostalCode';

type BillingAddress = {
  address: string;
  postCode: string;
  city: string;
  country: string;
};
interface ErrorObject {
  [key: string]: string;
}

const PaymentForm = ({
  packageDetails,
  introPrice,
  recurringPrice,
  memberProfileData,
  memberAddressData,
  stripePublishableKey,
  handlePriceChange,
  articleRef,
  onCheckoutCompletedUrl,
  checkoutStateUuid,
  callBackUrl,
}: {
  packageDetails: ProductPackageDetailsDataType;
  introPrice: number;
  recurringPrice: number;
  memberProfileData: MemberPersonalInfo;
  memberAddressData: ProfileAddress;
  stripePublishableKey: string;
  handlePriceChange: (introPrice: number, recurringPrice: number) => void;
  articleRef?: string;
  onCheckoutCompletedUrl?: string;
  checkoutStateUuid: string;
  callBackUrl: string;
}) => {
  const initializeErrors: ErrorObject = {
    name: '',
    email: '',
    card: '',
  };

  const initializeVippsPaymentDataError: VippsPaymentDataErrorType = {
    phone: '',
  };

  const initializePhoneNumberDataError: PhoneNumberDataErrorType = {
    phone: '',
  };

  const initializeBillingAddressErrors: BillingAddressErrorType = {
    address: '',
    postCode: '',
    city: '',
    country: '',
    invoiceReference: '',
  };

  const { push } = useRouter();
  const tCheckout = useTranslations('checkout');
  const tApiMessageCode = useTranslations('apiMessageCode');
  const tCommon = useTranslations('common');

  const tenant = useTenant();
  const locale = useLocale();

  // Use the StripePaymentRef type instead of any
  const stripePaymentRef = useRef<StripePaymentRef>(null);

  const inputFieldConfig = tenant?.preference
    ?.inputFieldConfig as unknown as CheckoutStepInputFieldConfig[];

  const oauthProviders = tenant?.oauthProviders || [];

  const paymentScreenInputFieldConfig = inputFieldConfig.find(
    (item) => item.page === CHECKOUT_STATE_STATUS.PaymentCompleted,
  );

  const externalAndEInvoicePaymentMethods = [
    PAYMENT_PROVIDER.External,
    PAYMENT_PROVIDER.EfakturaAvtalegiroEhf,
  ];

  const isEnabledGiftSubscription = packageDetails.subscriptionPlan.enableGiftSubscription;

  const [errors, setErrors] = useState<ErrorObject>(initializeErrors);
  const [memberCompanyInfoErrors, setMemberCompanyInfoErrors] =
    useState<MemberCompanyInfoErrorType>({
      companyName: '',
      organizationNo: '',
    });
  const [errorMessage, setErrorMessage] = useState('');

  const [stripeFormState, setStripeFormState] = useState<'error' | 'ready' | 'loading'>('ready');
  const [fakturaFormState, setFakturaFormState] = useState<'error' | 'ready' | 'loading'>('ready');
  const [vippsFormState, setVippsFormState] = useState<'error' | 'ready' | 'loading'>('ready');
  const [netsFormState, setNetsFormState] = useState<'error' | 'ready' | 'loading'>('ready');
  const [invoiceFormState, setInvoiceFormState] = useState<'error' | 'ready' | 'loading'>('ready');
  const [email, setEmail] = useState(memberProfileData?.email || '');
  const [name, setName] = useState(memberProfileData?.fullName || '');
  const [reference, setReference] = useState(articleRef || '');
  const [checkoutCompletedUrl, setCheckoutCompletedUrl] = useState(onCheckoutCompletedUrl || '');
  const [purchasabilityWarningMessage, setPurchasabilityWarningMessage] = useState('');
  const [purchasabilityBlockedMessage, setPurchasabilityBlockedMessage] = useState('');
  const [userExists, setUserExists] = useState(false);
  const [userExistResponseLoading, setUserExistResponseLoading] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [preOrderCheckoutStateId, setPreOrderCheckoutStateId] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showLoginToCheckoutHelpText, setShowLoginToCheckoutHelpText] = useState(false);

  const [memberCompanyInfo, setMemberCompanyInfo] = useState<MemberAsCompanyType>({
    isCompany: memberProfileData?.isCompany || false,
    companyName: memberProfileData?.companyName || '',
    organizationNo: memberProfileData?.organizationNo || '',
  });
  const [paymentMethod, setSelectedPaymentMethod] = useState('');

  const [vippsPaymentData, setVippsPaymentData] = useState<VippsPaymentDataType>({
    phone: '',
  });
  const [vippsPaymentDataErrors, setVippsPaymentDataErrors] = useState<VippsPaymentDataErrorType>(
    initializeVippsPaymentDataError,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [checkoutStateData, setCheckoutStateData] = React.useState<CheckoutStateType>(
    {} as CheckoutStateType,
  );

  const [billingAddressErrors, setBillingAddressErrors] = useState<BillingAddressErrorType>(
    initializeBillingAddressErrors,
  );
  const [shouldAddBillingAddress, setShouldAddBillingAddress] = useState(false);

  let phoneWithCountryCode = '';
  if (memberProfileData?.phone) {
    const parsedPhone = parsePhoneNumberFromString(memberProfileData.phone ?? '');
    phoneWithCountryCode = parsedPhone?.nationalNumber ?? '';
  }

  const [phoneNumber, setPhoneNumber] = useState<string>(phoneWithCountryCode);

  const [phoneNumberDataErrors, setPhoneNumberDataErrors] = useState<PhoneNumberDataErrorType>(
    initializePhoneNumberDataError,
  );

  const optionalProducts = getFilteredOptionalProducts(packageDetails.products);

  const formattedOptionalProducts = optionalProducts.map((product) => {
    const { productId, properties } = product;

    return {
      productId,
      quantity: properties?.isRequired ? 1 : 0,
      shippingCountries: [],
    };
  });

  const [filteredOptionalProducts, setFilteredOptionalProducts] =
    useState<PlanProductType[]>(optionalProducts);

  const [selectedOptionalProducts, setSelectedOptionalProducts] = useState<
    OptionalProductAdjustQuantityType[]
  >([]);

  // Consolidate the shipping countries for each product.
  const consolidateShippingCountries = (checkoutProducts: CheckoutStateOptionalProductType[]) => {
    const productMap: Record<number, OptionalProductAdjustQuantityType> = {};

    // Iterate over the checkout products and group by productId.
    checkoutProducts.forEach((product) => {
      if (!productMap[product.productId]) {
        productMap[product.productId] = {
          productId: product.productId,
          quantity: 0,
          shippingCountries: [],
        };
      }

      productMap[product.productId].quantity += product.quantity;

      if (product.shippingCountry) {
        productMap[product.productId].shippingCountries.push(product.shippingCountry);
      }
    });

    // Convert the map back to an array.
    return Object.values(productMap);
  };

  // Update optional products with consolidated information.
  const updateOptionalProductsWithConsolidatedInfo = (
    optionalProducts: PlanProductType[],
    consolidatedProducts: OptionalProductAdjustQuantityType[],
  ): PlanProductType[] => {
    // Map to store consolidated products by productId for quick access
    const consolidatedMap = new Map<number, OptionalProductAdjustQuantityType>(
      consolidatedProducts.map((product) => [product.productId, product]),
    );

    // Iterate over optional products and enhance them with consolidated data
    return optionalProducts.map((product) => {
      const consolidated = consolidatedMap.get(product.productId);
      return {
        ...product,
        quantity: consolidated?.quantity || 0, // Append quantity, default to 0 if not found
        shippingCountries: consolidated?.shippingCountries || [], // Append shipping countries, default to empty array if not found
      };
    });
  };

  const isAnyProductSelected = (products: OptionalProductAdjustQuantityType[]): boolean => {
    if (products.length === 0) {
      return false;
    }

    return products.some((product) => product.quantity > 0);
  };

  useEffect(() => {
    const fetchCheckoutStateData = async () => {
      try {
        const response = await api.checkoutStateControllerFindByUuidV1(checkoutStateUuid, {});
        const responseData = response.data as unknown as CheckoutStateType;
        const responseError = response.error as ErrorResponse;

        if (responseError?.messageCode) {
          throw new Error(responseError.messageCode);
        }

        setCheckoutStateData(responseData);
        // restore reference from checkout state if it is not set
        if (!reference || reference === '') {
          setReference(responseData?.paymentData?.userData?.reference);
        }

        // restore onCheckoutCompletedUrl from checkout state if it is not set
        if (!checkoutCompletedUrl || checkoutCompletedUrl === '') {
          setCheckoutCompletedUrl(responseData?.paymentData?.userData?.onCheckoutCompletedUrl);
        }
      } catch (error) {
        setCheckoutStateData({} as CheckoutStateType);
      }
    };

    if (checkoutStateUuid) {
      fetchCheckoutStateData();
    } else {
      // If checkout state uuid is not present, We set the selected optional products from package products
      setSelectedOptionalProducts(formattedOptionalProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStateUuid]);

  useEffect(() => {
    try {
      if (reference !== '') {
        // if reference is set we store it in tab session storage for later use in case we lose it
        sessionStorage.setItem('reference', reference);
      } else if (checkoutStateUuid === '' && reference === '') {
        // if it is not set and checkoutStateUuid is not present we can restore reference from tab session storage
        const tabArticleRef = sessionStorage.getItem('reference');
        if (tabArticleRef && tabArticleRef !== '') {
          setReference(tabArticleRef);
        }
      }

      if (checkoutCompletedUrl !== '') {
        // if onCheckoutCompletedUrl is set we store it in tab session storage for later use in case we lose it
        sessionStorage.setItem('onCheckoutCompletedUrl', checkoutCompletedUrl);
      } else if (checkoutStateUuid === '' && checkoutCompletedUrl === '') {
        // if it is not set and checkoutStateUuid is not present we can restore onCheckoutCompletedUrl from tab session storage
        const tabCheckoutCompletedUrl = sessionStorage.getItem('onCheckoutCompletedUrl');
        if (tabCheckoutCompletedUrl && tabCheckoutCompletedUrl !== '') {
          setCheckoutCompletedUrl(tabCheckoutCompletedUrl);
        }
      }
    } catch (error) {
      // Silently handle any security exceptions from sessionStorage operations
      console.log('SessionStorage access denied');
    }
  }, [reference, checkoutStateUuid, checkoutCompletedUrl]);

  useEffect(() => {
    const fetchTotalIntroductionPrice = async () => {
      try {
        // Check if any products are selected.
        const hasSelectedProducts = isAnyProductSelected(selectedOptionalProducts);

        // If no products are selected, return early. As we don't need to calculate the price.
        if (!hasSelectedProducts) {
          handlePriceChange(packageDetails.price, packageDetails.recurringPrice); // Reset to initial package price
          return;
        }

        const payload = {
          packageId: packageDetails.id,
          products: normalizeProductQuantities(selectedOptionalProducts),
        };

        const response = await getTotalIntroductionPrice(payload);

        if (response?.messageCode) {
          throw new Error(response.messageCode);
        }

        handlePriceChange(response.price, response.recurringPrice);

        if (typeof window !== 'undefined') {
          const packagePrice = {
            price: response.price,
            recurringPrice: response.recurringPrice,
          };

          window.sessionStorage.setItem('packagePrice', JSON.stringify(packagePrice));
        }
      } catch (error) {
        const typedError = error as Error;

        const errorMessage = tApiMessageCode(typedError.message);

        setErrorMessage(errorMessage);
      }
    };

    fetchTotalIntroductionPrice();
  }, [selectedOptionalProducts]);

  useEffect(() => {
    if (Object.keys(checkoutStateData).length !== 0) {
      // If the user is not logged in, set the email and name from the checkout state data.
      if (!memberProfileData) {
        setEmail(checkoutStateData?.paymentData.userData.email || '');
        setName(checkoutStateData?.paymentData.userData.fullName || '');
      }

      setVippsPaymentData((prevState) => ({
        ...prevState,
        phone: checkoutStateData?.paymentData.userData.phone || '',
      }));

      const consolidatedProducts = consolidateShippingCountries(
        checkoutStateData?.paymentData.optionalProducts,
      );

      const updatedOptionalProducts = updateOptionalProductsWithConsolidatedInfo(
        filteredOptionalProducts,
        consolidatedProducts,
      );

      setFilteredOptionalProducts(updatedOptionalProducts);
      setSelectedOptionalProducts(consolidatedProducts);
    } else {
      // If the checkout state data is empty, set the selected optional products from the package products.
      setSelectedOptionalProducts(formattedOptionalProducts);
    }
  }, [checkoutStateData]);

  const checkIfFieldIsDisplayable = (fieldName: string) => {
    if (!paymentScreenInputFieldConfig) {
      return false;
    }
    const showfield =
      paymentScreenInputFieldConfig.systemFields.find((field) => field.name === fieldName)?.show ||
      false;
    return showfield;
  };

  const checkIfFieldIsRequired = (fieldName: string) => {
    if (!paymentScreenInputFieldConfig) {
      return false;
    }

    const requiredField =
      paymentScreenInputFieldConfig.systemFields.find((field) => field.name === fieldName)
        ?.required || false;
    return requiredField;
  };

  const handleChangeVippsPhoneNumber = (phoneNumber: string) => {
    setVippsPaymentData((prevState) => ({ ...prevState, phone: phoneNumber }));
    setVippsPaymentDataErrors((previousErrors) => {
      return {
        ...previousErrors,
        phone: '',
      };
    });
  };

  const handleMemberPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setPhoneNumberDataErrors((previousErrors) => {
      return {
        ...previousErrors,
        phone: '',
      };
    });
  };

  const handleQuantityChange = (productId: number, quantity: number) => {
    setSelectedOptionalProducts((previousProducts) => {
      const productIndex = previousProducts.findIndex((product) => product.productId === productId);

      if (productIndex === -1) {
        return previousProducts;
      }

      const updatedProducts = [...previousProducts];
      updatedProducts[productIndex] = {
        ...updatedProducts[productIndex],
        quantity,
      };

      return updatedProducts;
    });
  };

  const handleShippingCountriesChange = (productId: number, shippingCountries: string[]) => {
    setSelectedOptionalProducts((previousProducts) => {
      const productIndex = previousProducts.findIndex((product) => product.productId === productId);

      if (productIndex === -1) {
        return previousProducts;
      }

      const updatedProducts = [...previousProducts];
      updatedProducts[productIndex] = {
        ...updatedProducts[productIndex],
        shippingCountries,
      };

      return updatedProducts;
    });
  };

  const validateFormData = () => {
    let isDataValid = true;

    if (!name) {
      setErrors((previousErrors) => {
        return {
          ...previousErrors,
          name: tCheckout('shouldNotBeEmpty'),
        };
      });
      isDataValid = false;
    }

    if (!validateName(name)) {
      setErrors((previousErrors) => {
        return {
          ...previousErrors,
          name: tCheckout('invalidName'),
        };
      });
      isDataValid = false;
    }

    if (!validateEmail(email)) {
      setErrors((previousErrors) => {
        return {
          ...previousErrors,
          email: tCheckout('invalidEmail'),
        };
      });
      isDataValid = false;
    }

    if (!paymentMethod) {
      setErrors((previousErrors) => {
        return {
          ...previousErrors,
          card: tCheckout('emptyPaymentMethod'),
        };
      });
      isDataValid = false;
    }
    if (checkIfFieldIsRequired('memberType')) {
      if (memberCompanyInfo.isCompany && !memberCompanyInfo.companyName) {
        setMemberCompanyInfoErrors((previousErrors) => {
          return {
            ...previousErrors,
            companyName: tApiMessageCode('shouldNotBeEmpty'),
          };
        });
        isDataValid = false;
      }

      if (
        memberCompanyInfo.isCompany &&
        !validateOrganisationNo(memberCompanyInfo.organizationNo)
      ) {
        setMemberCompanyInfoErrors((previousErrors) => {
          return {
            ...previousErrors,
            organizationNo: tApiMessageCode('invalidOrgNo'),
          };
        });
        isDataValid = false;
      }
    }

    return { isInvalid: !isDataValid };
  };

  const validateDeliveryAddressData = () => {
    let isDataValid = true;

    // For external payment, we need to add billing address
    if (
      !shouldAddBillingAddress &&
      !externalAndEInvoicePaymentMethods.includes(paymentMethod as PAYMENT_PROVIDER)
    ) {
      return { isInvalid: false };
    }

    const { address, city, postCode, country } = billingAddress;

    if (!address) {
      setBillingAddressErrors((previousErrors) => {
        return {
          ...previousErrors,
          address: tApiMessageCode('shouldNotBeEmpty'),
        };
      });
      isDataValid = false;
    }

    if (!postCode) {
      setBillingAddressErrors((previousErrors) => {
        return {
          ...previousErrors,
          postCode: tApiMessageCode('shouldNotBeEmpty'),
        };
      });
      isDataValid = false;
    }

    if (postCode && !validatePostalCode(postCode) && country === 'NO') {
      setBillingAddressErrors((previousErrors) => {
        return {
          ...previousErrors,
          postCode: tApiMessageCode('norwegianPostCodeError'),
        };
      });
      isDataValid = false;
    }

    if (!city) {
      setBillingAddressErrors((previousErrors) => {
        return {
          ...previousErrors,
          city: tApiMessageCode('shouldNotBeEmpty'),
        };
      });
      isDataValid = false;
    }

    if (city && !validateCity(city)) {
      setBillingAddressErrors((previousErrors) => {
        return {
          ...previousErrors,
          city: tApiMessageCode('invalidCity'),
        };
      });
      isDataValid = false;
    }

    if (!country) {
      setBillingAddressErrors((previousErrors) => {
        return {
          ...previousErrors,
          country: tApiMessageCode('shouldNotBeEmpty'),
        };
      });
      isDataValid = false;
    }

    return { isInvalid: !isDataValid };
  };

  /**
   * Checks if the user's email is already associated with an active checkout state.
   * @param {string} email - The user's email address.
   * @returns {Promise<boolean>} - True if the email is valid and not associated with errors, otherwise false.
   */
  const updateCheckoutState = async (email: string): Promise<boolean> => {
    try {
      // We format the email to lowercase before sending it to the API.
      // Our system don't allow email to be Uppercase.
      const formattedEmail = (email || '').toLowerCase();

      const payload = {
        packageId: packageDetails.id,
        userData: {
          name,
          email: formattedEmail,
          fullName: name,
          reference,
          onCheckoutCompletedUrl: checkoutCompletedUrl,
          isCompany: memberCompanyInfo.isCompany,
          companyName: memberCompanyInfo.isCompany ? memberCompanyInfo.companyName : '',
          organizationNo: memberCompanyInfo.isCompany ? memberCompanyInfo.organizationNo : '',
        },
        isSaved: true,
        optionalProducts: normalizeProductQuantities(selectedOptionalProducts),
        billingAddress: billingAddress ? billingAddress : undefined,
      };
      // Prepare the data payload.
      const payloadData: AccountCheckDto = {
        email,
        data: payload,
      };

      // Perform the API call to check the email's checkout state.
      const emailExistsResponse = await api.checkoutStateControllerAccountCheckUpdateV1(
        preOrderCheckoutStateId,
        payloadData,
      );
      const emailExistErrorResponse = emailExistsResponse.error;

      // If the API returns an error message code, throw an error.
      if (emailExistErrorResponse?.messageCode) {
        throw new Error(emailExistErrorResponse.messageCode);
      }

      // Return true if there are no issues with the API response.
      return true;
    } catch (error) {
      // On error, log and set the error message, then return false.
      const typedError = error as Error;
      setErrorMessage(tApiMessageCode(typedError.message));
      return false;
    }
  };

  /**
   * Handles the login procedure by first verifying the checkout state and then initiating the
   *  login process.
   */
  const handleLogin = async () => {
    try {
      // Check if checkout state needs to be updated.
      const shouldUpdateCheckoutState = await updateCheckoutState(email);

      if (!shouldUpdateCheckoutState) {
        throw new Error('Failed to update checkout state');
      }

      // Set loading state true during the login process.
      setIsLoginLoading(true);

      // Prepare callback URLs for successful and failed login attempts.
      const codeUrl = '/auth/signin/code/';
      const callbackUrl = new URL(
        `${locale}/checkout/${packageDetails.slug}`,
        `https://${window.location.host}`, // Dummy protocol. Not used.
      );
      const failUrl = new URL('/auth/signin', window.location.href);

      // Append the checkoutStateUuid to the URLs if present.
      if (preOrderCheckoutStateId) {
        callbackUrl.searchParams.append('checkoutStateUuid', preOrderCheckoutStateId);
        failUrl.searchParams.append('checkoutStateUuid', preOrderCheckoutStateId);
      }

      const callbackUrlString = `${callbackUrl.pathname}${callbackUrl.search}`;

      // If there are OAuth providers, navigate to the sign-in page with the email and callback URL.
      if (oauthProviders.length > 0) {
        const url = new URL('/auth/signin', window.location.href);
        url.searchParams.append('email', email);
        url.searchParams.append('callbackUrl', callbackUrlString);

        // If login is successful, navigate to the callback URL.
        push(url.pathname + url.search);
        return;
      }

      try {
        // Attempt to sign in.
        const response = await signIn('email', {
          email: email,
          redirect: false,
        });

        if (response?.status === 200) {
          const url = new URL(codeUrl, window.location.href);
          url.searchParams.append('email', email);
          url.searchParams.append('callbackUrl', callbackUrlString);

          // If login is successful, navigate to the callback URL.
          push(url.pathname + url.search);
        } else {
          // On failure, navigate to the fail URL and reset the loading state.
          push(failUrl.toString());
          setIsLoginLoading(false);
        }
      } catch (error) {
        // On failure, navigate to the fail URL and reset the loading state.
        push(failUrl.toString());
        setIsLoginLoading(false);
      }
    } catch (error) {
      // Handle any errors that occur during the login process.
      const errorMessage = tApiMessageCode('updateFailed');

      setErrorMessage(errorMessage);
    }
  };

  const checkUserExists = async (email: string) => {
    // If user email is not valid, return
    if (!validateEmail(email)) {
      return;
    }

    // If user exists true and email is valid, return
    if (validateEmail(email) && userExists) {
      return;
    }

    // Only check if found userExist false and email is valid

    try {
      setUserExistResponseLoading(true);

      // We format the email to lowercase before sending it to the API.
      // Our system don't allow email to be Uppercase.
      const formattedEmail = (email || '').toLowerCase();

      const payload = {
        packageId: packageDetails.id,
        userData: {
          name,
          email: formattedEmail,
          fullName: name,
          reference,
          onCheckoutCompletedUrl: checkoutCompletedUrl,
          isCompany: memberCompanyInfo.isCompany,
          companyName: memberCompanyInfo.isCompany ? memberCompanyInfo.companyName : '',
          organizationNo: memberCompanyInfo.isCompany ? memberCompanyInfo.organizationNo : '',
        },
        isSaved: true,
        optionalProducts: normalizeProductQuantities(selectedOptionalProducts),
        billingAddress: billingAddress ? billingAddress : undefined,
      };

      const payloadData: AccountCheckDto = {
        email: email,
        data: payload,
      };

      const emailExistsResponse = await api.checkoutStateControllerAccountCheckV1(payloadData);

      const emaiLExistErrorResponse = emailExistsResponse.error;
      const emailExistsResponseData = emailExistsResponse.data;

      if (emaiLExistErrorResponse?.messageCode) {
        setUserExists(false);
        setUserExistResponseLoading(false);
        return;
      }

      if (emailExistsResponseData.status === AccountCheckStatus.EXISTING_MEMBER) {
        setUserExists(true);
        // Set the pre-order checkout state id and clear the selected payment method.
        setSelectedPaymentMethod('');
        setPreOrderCheckoutStateId(emailExistsResponseData?.checkoutStateUUID);
        setUserExistResponseLoading(false);
        return;
      }

      setUserExists(false);
      setPreOrderCheckoutStateId('');
      setUserExistResponseLoading(false);
    } catch (error) {
      setUserExists(false);
      setUserExistResponseLoading(false);
      setPreOrderCheckoutStateId('');
    }
  };

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(e.target.value);
    setErrors((prevState) => ({ ...prevState, email: '' }));
    setUserExists(false);
    setShowLoginToCheckoutHelpText(false);
  };

  const handleBlurEmail = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value) {
      // Simple validation for email, improve as needed
      checkUserExists(e.target.value);
    }
  };

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setErrors((prevState) => ({ ...prevState, name: '' }));
  };

  const handleMemberAsCompany = (isCompany: boolean) => {
    setMemberCompanyInfo((prevState) => ({
      ...prevState,
      isCompany: isCompany,
    }));
  };

  const handleMemberCompanyInfo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setMemberCompanyInfo((prevState) => ({ ...prevState, [name]: value }));
    setMemberCompanyInfoErrors((previousErrors) => {
      return {
        ...previousErrors,
        [name]: '',
      };
    });
  };

  const handleChangePaymentMethod = (paymentMethodId: string) => {
    setSelectedPaymentMethod(paymentMethodId);
    setBillingAddress((prev) => ({
      ...prev,
      addressTwo: '',
    }));
    setErrors((prevState) => ({ ...prevState, card: '' }));
    setBillingAddressErrors(initializeBillingAddressErrors);

    // Is payment method is external or invoice, We should add billing address
    if (externalAndEInvoicePaymentMethods.includes(paymentMethodId as PAYMENT_PROVIDER)) {
      setShouldAddBillingAddress(true);
    }
  };

  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    address: memberAddressData?.line1 || '',
    address2: memberAddressData?.line2 || '',
    postCode: memberAddressData?.postCode || '',
    city: memberAddressData?.city || '',
    country: memberAddressData?.country || 'NO',
  } as BillingAddress);

  const handleShouldAddBillingAddress = (e: ChangeEvent<HTMLInputElement>) => {
    setShouldAddBillingAddress(e.target.checked);

    if (!e.target.checked) {
      setBillingAddress({
        address: '',
        addressTwo: '',
        postCode: '',
        city: '',
        country: 'NO',
      } as BillingAddress);
      setBillingAddressErrors(initializeBillingAddressErrors);
    }
  };

  const handleChangeBillingAddress = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setBillingAddress((prevState) => ({ ...prevState, [name]: value }));
    setBillingAddressErrors((previousErrors) => {
      return {
        ...previousErrors,
        [name]: '',
      };
    });
  };

  const handleSetStripeFromState = (state: 'error' | 'ready' | 'loading') => {
    setStripeFormState(state);
  };

  const handleSetPaymentLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  const handleSetErrorMessage = (message: string) => {
    setErrorMessage(message);
  };

  const submitNetsForm = async () => {
    setNetsFormState('loading');
    setErrorMessage('');

    try {
      const payload = {
        packageId: packageDetails.id,
        userData: {
          name,
          email,
          reference,
          onCheckoutCompletedUrl: checkoutCompletedUrl || '',
          phone: vippsPaymentData.phone,
          fullName: name,
          isCompany: memberCompanyInfo.isCompany,
          companyName: memberCompanyInfo.isCompany ? memberCompanyInfo.companyName : '',
          organizationNo: memberCompanyInfo.isCompany ? memberCompanyInfo.organizationNo : '',
        },
        isSaved: true,
        optionalProducts: normalizeProductQuantities(selectedOptionalProducts),
        ...(shouldAddBillingAddress && { billingAddress }),
      };

      const response = await api.netsControllerCreateIntentV1(payload, {});

      const responseData = (response.data || response.error) as NetsCreateIntentResponse &
        ErrorResponse;

      if (responseData?.messageCode) {
        throw new Error(responseData.messageCode);
      }

      if (responseData.hostedPaymentPageUrl) {
        push(responseData.hostedPaymentPageUrl);
      } else {
        const errorMessage = tApiMessageCode('vippsConfirmationURLmissing');

        setErrorMessage(errorMessage);
        setNetsFormState('error');
      }

      setNetsFormState('ready');
    } catch (error) {
      const errorMessage = tApiMessageCode('paymentFailed');

      setErrorMessage(errorMessage);
      setNetsFormState('error');
    }
  };

  const submitVippsForm = async () => {
    if (!validateMobilePhoneNr(vippsPaymentData.phone)) {
      setVippsPaymentDataErrors((previousErrors) => {
        return {
          ...previousErrors,
          phone: tCheckout('invalidPhone'),
        };
      });
      return;
    }

    setVippsFormState('loading');
    setErrorMessage('');

    try {
      const payload = {
        packageId: packageDetails.id,
        userData: {
          name,
          email,
          reference,
          onCheckoutCompletedUrl: checkoutCompletedUrl,
          phone: vippsPaymentData.phone,
          fullName: name,
          isCompany: memberCompanyInfo.isCompany,
          companyName: memberCompanyInfo.isCompany ? memberCompanyInfo.companyName : '',
          organizationNo: memberCompanyInfo.isCompany ? memberCompanyInfo.organizationNo : '',
        },
        isSaved: true,
        optionalProducts: normalizeProductQuantities(selectedOptionalProducts),
        ...(shouldAddBillingAddress && { billingAddress }),
      };

      const response = await api.vippsControllerCreateIntentV1(payload, {});

      const responseData = (response.data || response.error) as VippsCreateIntentResponse &
        ErrorResponse;

      if (responseData?.messageCode) {
        throw new Error(responseData.messageCode);
      }

      if (responseData.vippsConfirmationUrl) {
        push(responseData.vippsConfirmationUrl);
      } else {
        const errorMessage = tApiMessageCode('vippsConfirmationURLmissing');

        setErrorMessage(errorMessage);
        setVippsFormState('error');
      }

      setVippsFormState('ready');
    } catch (error) {
      const errorMessage = tApiMessageCode('paymentFailed');

      setErrorMessage(errorMessage);
      setVippsFormState('error');
    }
  };

  const submitFakturaForm = async () => {
    setFakturaFormState('loading');
    setErrorMessage('');
    setIsLoading(true);

    try {
      const payload = {
        packageId: packageDetails.id,
        userData: {
          email,
          fullName: name,
          reference,
          onCheckoutCompletedUrl: checkoutCompletedUrl,
          isCompany: memberCompanyInfo.isCompany,
          companyName: memberCompanyInfo.isCompany ? memberCompanyInfo.companyName : '',
          organizationNo: memberCompanyInfo.isCompany ? memberCompanyInfo.organizationNo : '',
        },
        optionalProducts: normalizeProductQuantities(selectedOptionalProducts),
        ...(shouldAddBillingAddress && { billingAddress }),
      };

      const response = await api.paymentControllerSubscribeViaFakturaV1(payload);

      const responseError = response.error;

      if (responseError?.messageCode && !responseError?.errors?.length) {
        throw new Error(responseError.messageCode);
      } else if (responseError?.errors?.length) {
        // Handle the errors
        const { errors } = responseError;

        let dataErrors = initializeErrors;
        if (errors && errors.length) {
          errors.forEach((error: FieldError) => {
            const { field, messageCode } = error;
            dataErrors = {
              ...dataErrors,
              [field]: tApiMessageCode(messageCode),
            };
          });

          setErrors(dataErrors);
          setFakturaFormState('error');
          setErrorMessage(tApiMessageCode('createFailed'));
          setIsLoading(false);
          return;
        }
      }

      const responseData = response.data as FakturaPaymentResponseDto;

      push(
        `/checkout/${
          packageDetails.slug
        }/auth/external-payment/complete?checkoutStateUuid=${encodeURIComponent(
          responseData.checkoutStateUuid || '',
        )}`,
      );
      setIsLoading(false);
      setFakturaFormState('ready');
    } catch (error) {
      const typedError = error as Error;

      const errorMessage = tApiMessageCode(typedError.message);

      setErrorMessage(errorMessage);
      setFakturaFormState('error');
      setIsLoading(false);
    }
  };

  const submitInvoiceForm = async () => {
    if (!validateMobilePhoneNr(`+47${phoneNumber}`)) {
      setPhoneNumberDataErrors((previousErrors) => {
        return {
          ...previousErrors,
          phone: tCheckout('invalidPhone'),
        };
      });
      return;
    }
    setInvoiceFormState('loading');
    setErrorMessage('');
    setIsLoading(true);

    try {
      const payload = {
        packageId: packageDetails.id,
        userData: {
          email,
          fullName: name,
          reference,
          onCheckoutCompletedUrl: checkoutCompletedUrl,
          isCompany: memberCompanyInfo.isCompany,
          companyName: memberCompanyInfo.isCompany ? memberCompanyInfo.companyName : '',
          organizationNo: memberCompanyInfo.isCompany ? memberCompanyInfo.organizationNo : '',
          phone: phoneNumber,
        } as CheckoutUser,
        optionalProducts: normalizeProductQuantities(selectedOptionalProducts),
        ...(shouldAddBillingAddress && { billingAddress }),
      };

      // TODO: Will remove strcit type declaration after refactoring the API
      const response = await api.eInvoiceControllerSubscribeViaEInvoiceV1(
        payload as unknown as CreateFakturaPaymentPayload,
      );
      const responseData = response.data as FakturaPaymentResponseDto;
      const responseError = response.error as ErrorResponse;

      if (responseError?.messageCode && !responseError?.errors?.length) {
        throw new Error(responseError.messageCode);
      } else if (responseError?.errors?.length) {
        // Handle the errors
        const { errors } = responseError;

        let dataErrors = initializeErrors;
        if (errors && errors.length) {
          errors.forEach((error: FieldError) => {
            const { field, messageCode } = error;
            dataErrors = {
              ...dataErrors,
              [field]: tApiMessageCode(messageCode),
            };
          });

          setErrors(dataErrors);
          setInvoiceFormState('error');
          setErrorMessage(tApiMessageCode('createFailed'));
          setIsLoading(false);
          return;
        }
      }

      push(
        `/checkout/${
          packageDetails.slug
        }/auth/e-invoice-payment/complete?checkoutStateUuid=${encodeURIComponent(
          responseData.checkoutStateUuid || '',
        )}`,
      );
      setIsLoading(false);
      setInvoiceFormState('ready');
    } catch (error) {
      const typedError = error as Error;

      const errorMessage = tApiMessageCode(typedError.message);

      setErrorMessage(errorMessage);
      setInvoiceFormState('error');
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    const { isInvalid } = validateDeliveryAddressData();

    if (isInvalid) {
      return;
    }

    if (paymentMethod === PAYMENT_PROVIDER.Stripe) {
      if (stripePaymentRef.current) {
        // Call the submitCardForm method from the child component
        stripePaymentRef.current.submitCardForm();
      }
    } else if (paymentMethod === PAYMENT_PROVIDER.External) {
      submitFakturaForm();
    } else if (paymentMethod === PAYMENT_PROVIDER.Vipps) {
      submitVippsForm();
    } else if (paymentMethod === PAYMENT_PROVIDER.Nets) {
      submitNetsForm();
    } else if (paymentMethod === PAYMENT_PROVIDER.EfakturaAvtalegiroEhf) {
      submitInvoiceForm();
    }
  };

  const handlePackagePurchasabilityCheck = async (event: FormEvent) => {
    event.preventDefault();
    // if user is not logged in, and user exists, we restrict to make order
    if (!memberProfileData && userExists) {
      setShowLoginToCheckoutHelpText(true);
      return;
    }

    const { isInvalid } = validateFormData();

    if (isInvalid) {
      return;
    }

    const samePackagePurchasePermissionLevel =
      tenant?.preference.permissionLevelForBuyingSamePackage;
    const multipleSubscriptionPlansPurchasePermissionLevel =
      tenant?.preference.permissionLevelForBuyingMultipleSubscriptions;

    // If both permission levels are allowed OR if it's a Gift subscription, user can proceed with the order
    if (
      ((samePackagePurchasePermissionLevel === PermissionLevelForBuyingSamePackage.Allow &&
        multipleSubscriptionPlansPurchasePermissionLevel ===
          PermissionLevelForBuyingMultipleSubscriptions.Allow) ||
        isEnabledGiftSubscription) &&
      formRef.current
    ) {
      handleSubmit();
      return;
    }

    try {
      // if any of the permission levels are not allowed, we need to fetch associated API's to check package purchasability

      const packagesWithDuplicateSlugPromise = api.packageControllerGetDuplicatedPackagesWithSlugV1(
        tenant?.id as number,
        packageDetails.slug,
        { email },
      );
      const packagesWithSubscriptionPlansPromise =
        api.packageControllerGetPackagesWithSubscriptionPlansV1(tenant?.id as number, { email });

      const [packagesWithDuplicateSlugResponse, packagesWithSubscriptionPlansResponse] =
        await Promise.all([packagesWithDuplicateSlugPromise, packagesWithSubscriptionPlansPromise]);

      const packagesWithDuplicateSlug = packagesWithDuplicateSlugResponse?.data;
      const packagesWithSubscriptionPlans = packagesWithSubscriptionPlansResponse?.data;

      // all possible messages depending on the combinations
      const samePackagePurchaseAbilityWarningMessage = tCheckout(
        'samePackagePurchaseAbilityWarningMessage',
      );

      const samePackagePurchaseAbilityBlockedMessage = tCheckout(
        'samePackagePurchaseAbilityBlockedMessage',
        {
          customerServiceLink: tenant?.tenantContact ?? '',
        },
      );

      const multipleSubscriptionPlanPurchaseAbilityWarningMessage = tCheckout(
        'multipleSubscriptionPlanPurchaseAbilityWarningMessage',
      );

      const multipleSubscriptionPlanPurchaseAbilityBlockedMessage = tCheckout(
        'multipleSubscriptionPlanPurchaseAbilityBlockedMessage',
        {
          customerServiceLink: tenant?.tenantContact ?? '',
        },
      );

      const purchasabilityWarningMessageForAllCheck = tCheckout(
        'purchasabilityWarningMessageForAllCheck',
      );

      const purchasabilityBlockedMessageForAllCheck = tCheckout(
        'purchasabilityBlockedMessageForAllCheck',
        {
          customerServiceLink: tenant?.tenantContact ?? '',
        },
      );

      // get the warning and block messages depending on different combintations
      const { warningMessage, blockedMessage } = getPackagePurchasingBlockAndWarningMessage({
        samePackagePurchasePermissionLevel,
        multipleSubscriptionPlansPurchasePermissionLevel,
        packagesWithDuplicateSlug,
        packagesWithSubscriptionPlans,
        samePackagePurchaseAbilityWarningMessage,
        samePackagePurchaseAbilityBlockedMessage,
        multipleSubscriptionPlanPurchaseAbilityWarningMessage,
        multipleSubscriptionPlanPurchaseAbilityBlockedMessage,
        purchasabilityWarningMessageForAllCheck,
        purchasabilityBlockedMessageForAllCheck,
      });

      // if there is not block or warning message proceed with the order
      if (!warningMessage && !blockedMessage && formRef.current) {
        handleSubmit();
        return;
      }

      if (warningMessage) setPurchasabilityWarningMessage(warningMessage);
      else if (blockedMessage) setPurchasabilityBlockedMessage(blockedMessage);
    } catch {
      setErrorMessage(tApiMessageCode('failedToFetchData'));
    }
  };

  const handleCancelingPurchaseAbilityBlock = () => {
    setPurchasabilityBlockedMessage('');
  };

  const handleCancelingPurchaseAbilityWarning = () => {
    setPurchasabilityWarningMessage('');
  };

  const handlePurchaseAbilityWarning = () => {
    setPurchasabilityWarningMessage('');
    if (formRef.current) {
      handleSubmit();
    }
  };

  const handleCancelCheckout = () => {
    push(callBackUrl || '/');
  };

  const isError =
    stripeFormState === 'error' ||
    fakturaFormState === 'error' ||
    vippsFormState === 'error' ||
    netsFormState === 'error' ||
    invoiceFormState === 'error';

  const isStripeProvider = packageDetails.paymentProviders.includes(PAYMENT_PROVIDER.Stripe);
  const isVippsProvider = packageDetails.paymentProviders.includes(PAYMENT_PROVIDER.Vipps);
  const isExternalProvider = packageDetails.paymentProviders.includes(PAYMENT_PROVIDER.External);
  const isNetsProvider = packageDetails.paymentProviders.includes(PAYMENT_PROVIDER.Nets);
  const isInvoiceProvider = packageDetails.paymentProviders.includes(
    PAYMENT_PROVIDER.EfakturaAvtalegiroEhf,
  );

  const isStripePaymentLoading = isStripeProvider && stripeFormState === 'loading';
  const isExternalPaymentLoading = isExternalProvider && fakturaFormState === 'loading';
  const isVippsPaymentLoading = isVippsProvider && vippsFormState === 'loading';
  const isNetsPaymentLoading = isNetsProvider && netsFormState === 'loading';
  const isInvoicePaymentLoading = isInvoiceProvider && invoiceFormState === 'loading';

  const isOrderedButtonLoading =
    isLoading ||
    isExternalPaymentLoading ||
    isVippsPaymentLoading ||
    isNetsPaymentLoading ||
    isInvoicePaymentLoading ||
    isStripePaymentLoading;

  const isDisabledOrderButton: boolean = isOrderedButtonLoading || isLoginLoading;

  return (
    // TODO: Need to refactor this Context Provider, to pass as less props as possible
    <PaymentFormProvider
      payementMethod={paymentMethod}
      cardError={errors.card}
      paymentProviders={packageDetails.paymentProviders}
      memberCompanyInfo={memberCompanyInfo}
      memberCompanyInfoErrors={memberCompanyInfoErrors}
      vippsPaymentData={vippsPaymentData}
      vippsPaymentDataErrors={vippsPaymentDataErrors}
      billingAddressData={billingAddress}
      billingAddressErrors={billingAddressErrors}
      phoneNumberDataErrors={phoneNumberDataErrors}
      shouldAddBillingAddress={shouldAddBillingAddress}
      memberPhoneNumber={phoneNumber}
      handleChangeVippsPhoneNumber={handleChangeVippsPhoneNumber}
      handleChangePaymentMethod={handleChangePaymentMethod}
      handleChangeMemberAsCompany={handleMemberAsCompany}
      handleChangeMemberCompanyInfo={handleMemberCompanyInfo}
      handleChangeBillingAddressData={handleChangeBillingAddress}
      handleShouldAddBillingAddress={handleShouldAddBillingAddress}
      handlePhoneNumberChange={handleMemberPhoneNumberChange}
    >
      <div className="text-base leading-7 text-gray-700 lg:max-w-lg">
        {isError && <p className="text-center text-red font-bold mt-8">{errorMessage}</p>}

        {purchasabilityBlockedMessage && (
          <ConfirmationDialog
            shouldOpen={Boolean(purchasabilityBlockedMessage)}
            closeModalOnBackdropClick={false}
            title={tCheckout('purchaseAbilityBlockedTitle')}
            description={purchasabilityBlockedMessage}
            onCancel={handleCancelingPurchaseAbilityBlock}
            btnTextOnCancel={tCommon('cancel')}
          />
        )}

        {purchasabilityWarningMessage && (
          <ConfirmationDialog
            shouldOpen={Boolean(purchasabilityWarningMessage)}
            closeModalOnBackdropClick={false}
            title={tCheckout('purchaseAbilityWarningTitle')}
            description={purchasabilityWarningMessage}
            onProceed={handlePurchaseAbilityWarning}
            onCancel={handleCancelingPurchaseAbilityWarning}
            btnTextOnProceed={tCheckout('order', {
              total_amount: introPrice,
            })}
          />
        )}

        <form ref={formRef}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-6">
              <div className="sm:col-span-6">
                <ul role="list" className="space-y-3 mt-2">
                  {filteredOptionalProducts.map((product) => (
                    <li role="listitem" key={product.productId} className="overflow-hidden">
                      <OptionalProduct
                        introBillingPeriod={packageDetails.billingPeriod}
                        introBillingPeriodUnit={packageDetails.billingPeriodUnit}
                        introBillingPeriodFrequency={packageDetails.billingPeriodFrequency}
                        product={product}
                        plan={packageDetails.subscriptionPlan}
                        introPeriodFixedEndDate={packageDetails.introPeriodFixedEndDate}
                        onChangeQuantity={handleQuantityChange}
                        onChangeShippingCountries={handleShippingCountriesChange}
                      />
                    </li>
                  ))}
                </ul>
              </div>
              <TotalPriceInformation
                introPrice={introPrice}
                recurringPrice={recurringPrice}
                packageDetails={packageDetails}
              />
              {isEnabledGiftSubscription && (
                <div className="sm:col-span-6 flex flex-col">
                  <span className="text-gray-600 text-sm">
                    {tCheckout('giftSubscriptionProcessHelpText')}
                  </span>
                </div>
              )}
              <PayingMemberInfo
                userExistResponseLoading={userExistResponseLoading}
                userExists={userExists}
                isLoginLoading={isLoginLoading}
                handleLogin={handleLogin}
                checkIfFieldIsDisplayable={checkIfFieldIsDisplayable}
                name={name}
                email={email}
                handleChangeName={handleChangeName}
                handleChangeEmail={handleChangeEmail}
                handleBlurEmail={handleBlurEmail}
                errors={errors}
              />
              <div className="sm:col-span-6">
                <div className="flex justify-between">
                  <label htmlFor="payment-method" className="font-bold text-sm">
                    {tCheckout('paymentMethod').toUpperCase()}
                  </label>
                  <span className="flex items-center">
                    <Secure />
                    <span className="pl-2 text-outerSpace font-medium text-sm">
                      {tCheckout('secure')}
                    </span>
                  </span>
                </div>
                <div className="mt-1">
                  <PaymentMethods
                    packageId={packageDetails.id}
                    checkoutCompletedUrl={checkoutCompletedUrl}
                    reference={reference}
                    fullName={name}
                    email={email}
                    slug={packageDetails.slug}
                    selectedOptionalProducts={selectedOptionalProducts}
                    stripePaymentRef={stripePaymentRef as RefObject<StripePaymentRef>}
                    handleSetErrorMessage={handleSetErrorMessage}
                    handleSetStripeFromState={handleSetStripeFromState}
                    handleSetPaymentLoading={handleSetPaymentLoading}
                    shouldDisablePaymentMethod={userExists}
                    introPrice={introPrice}
                    recurringPrice={recurringPrice}
                    stripePublishableKey={stripePublishableKey}
                    introPeriodFixedEndDate={packageDetails.introPeriodFixedEndDate}
                  />
                </div>
              </div>
              <TermsConditions />
              <div className="sm:col-span-6">
                {showLoginToCheckoutHelpText && (
                  <p className="text-red text-sm mt-2 mb-2">
                    {tCheckout('loginToContinue.checkoutHelpText')}
                  </p>
                )}
                <button
                  type="button"
                  data-testid="orderButton"
                  disabled={isDisabledOrderButton}
                  className={clsx(
                    isDisabledOrderButton ? 'cursor-not-allowed' : 'cursor-pointer',
                    'flex w-full items-center font-bold justify-center rounded-3xl border border-transparent bg-[var(--themePrimaryButtonColor)] px-[10px] py-[10px] text-[var(--themePrimaryButtonTextColor)] hover:bg-[var(--themePrimaryHoverColor)] focus:outline-none focus:ring-2 focus:ring-[var(--themePrimaryButtonColor)] focus:ring-offset-2',
                  )}
                  onClick={handlePackagePurchasabilityCheck}
                >
                  {isOrderedButtonLoading ? (
                    <Loader />
                  ) : (
                    tCheckout('order', {
                      total_amount: introPrice,
                    })
                  )}
                </button>
              </div>
              <div className="sm:col-span-6">
                <button
                  type="button"
                  className="flex w-full font-medium text-base items-center justify-center rounded-md border border-transparent bg-transparent py-2 text-outerSpace hover:bg-white focus:outline-none"
                  onClick={handleCancelCheckout}
                >
                  {tCheckout('cancel')}
                </button>
              </div>
              <FooterText isAutoRenewOn={packageDetails.subscriptionPlan.autoRenew} />
            </div>
          </div>
        </form>
      </div>
    </PaymentFormProvider>
  );
};

export default PaymentForm;
